import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import {
  Box,
  Button,
  Checkbox,
  Loader,
  RegularTextField,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { AddCircleOutlineIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useAddFreelancersToRosters } from "@js/apps/rosters/hooks/use-add-freelancers-to-rosters";
import { ModalInstance } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { RhfTextareaField } from "@js/rhf/fields";
import { pluralize } from "@js/utils/string";

import { useCreateRosterMutation, useGetRostersQuery } from "../../api";
import type { RosterSummary } from "../../types";
import { RosterVisibilityChip } from "../roster-visibility-chip";

import styles from "./styles.module.scss";

const addToRosterFormSchema = z.object({
  note: z.string().optional(),
  selectedIds: z.array(z.number()),
});

type AddToRosterFormValues = z.infer<typeof addToRosterFormSchema>;

export type AddToRosterModalContentProps = {
  freelancerIds: number[];
  onSuccess?: () => void;
};

export const openAddToRosterModal = (props: AddToRosterModalContentProps) =>
  ModalInstance.open({
    closeButton: true,
    keepOnBackdropClick: true,
    children: <AddToRosterModalContent {...props} />,
    className: styles.modal,
  });

export const AddToRosterModalContent = ({
  freelancerIds,
  onSuccess,
}: AddToRosterModalContentProps) => {
  const { data, isLoading } = useGetRostersQuery({ page_size: 100 });
  const { addFreelancersToRosters } = useAddFreelancersToRosters();

  const { handleSubmit, control, formState } = useForm<AddToRosterFormValues>({
    mode: "onTouched",
    resolver: zodResolver(addToRosterFormSchema),
    defaultValues: { note: "", selectedIds: [] },
  });
  const selectedIds = useWatch({ control, name: "selectedIds" });

  const onSubmit = async ({
    note,
    selectedIds: rosterIds,
  }: AddToRosterFormValues) => {
    const isSuccess = await addFreelancersToRosters({
      rosterIds,
      freelancerIds,
      note: note?.trim(),
    });

    if (!isSuccess) {
      return;
    }

    onSuccess?.();
    ModalInstance.close();
  };

  return (
    <Box>
      <Typography component="h2" variant="title" size="small">
        Add to roster
      </Typography>
      <Typography component="p" variant="paragraph" sx={{ mb: 3 }}>
        You&apos;re adding {freelancerIds.length} talent
        {pluralize(freelancerIds.length)} to {selectedIds.length} roster
        {pluralize(selectedIds.length)}.
      </Typography>
      {isLoading ? (
        <Loader centered />
      ) : (
        <Controller
          name="selectedIds"
          control={control}
          render={({ field }) => (
            <RosterCheckboxList
              rosters={data?.results ?? []}
              selectedIds={field.value}
              onToggleRoster={(id) => {
                field.onChange(
                  field.value.includes(id)
                    ? field.value.filter((selectedId) => selectedId !== id)
                    : [...field.value, id],
                );
              }}
            />
          )}
        />
      )}
      <Controller
        name="selectedIds"
        control={control}
        render={({ field }) => (
          <NewRosterField
            onCreateRoster={(id) => field.onChange([...field.value, id])}
          />
        )}
      />
      <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ mb: 3 }}>
          <RhfTextareaField
            control={control}
            id="note"
            name="note"
            label="Note (optional)"
            placeholder="Add a note for these freelancers on the roster(s)"
            minRows={2}
            fullWidth
          />
        </Box>
        <Button
          sx={{ ml: "auto" }}
          variant="positive"
          shape="squared"
          size="medium"
          type="submit"
          disabled={!selectedIds.length || formState.isSubmitting}
        >
          {selectedIds.length
            ? `Add to ${selectedIds.length} roster${pluralize(selectedIds.length)}`
            : "Add to roster"}
        </Button>
      </Stack>
    </Box>
  );
};

type RosterCheckboxListProps = {
  rosters: RosterSummary[];
  selectedIds: number[];
  onToggleRoster: (id: number) => void;
};

const RosterCheckboxList = ({
  rosters,
  selectedIds,
  onToggleRoster,
}: RosterCheckboxListProps) => (
  <Stack sx={{ gap: 1, maxHeight: "320px", overflowY: "auto", mb: 3 }}>
    {rosters.map((roster) => (
      <Stack
        key={roster.id}
        direction="row"
        sx={{ alignItems: "center", gap: 1 }}
      >
        <Checkbox
          checked={selectedIds.includes(roster.id)}
          onChange={() => onToggleRoster(roster.id)}
          aria-label={roster.title}
          noMargin
        />
        <Typography
          component="span"
          variant="paragraph"
          sx={{ flexGrow: 1, cursor: "pointer" }}
          onClick={() => onToggleRoster(roster.id)}
        >
          {roster.title}
        </Typography>
        <RosterVisibilityChip visibility={roster.visibility} />
      </Stack>
    ))}
    {!rosters.length && (
      <Typography component="p" variant="paragraph">
        You don’t have any rosters yet — create one below.
      </Typography>
    )}
  </Stack>
);

type NewRosterFieldProps = {
  onCreateRoster: (id: number) => void;
};

const NewRosterField = ({ onCreateRoster }: NewRosterFieldProps) => {
  const [title, setTitle] = useState("");
  const [createRoster] = useCreateRosterMutation();

  const handleCreateAndSelect = async () => {
    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      return;
    }

    try {
      const roster = await createRoster({ title: trimmedTitle }).unwrap();
      setTitle("");
      onCreateRoster(roster.id);
    } catch {
      Snackbar.error("Something went wrong creating the roster.");
    }
  };

  return (
    <Stack direction="row" sx={{ gap: 1, alignItems: "center", mb: 3 }}>
      <RegularTextField
        size="small"
        fullWidth
        placeholder="New roster title"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <Button
        variant="white-border-beige"
        shape="squared"
        size="small"
        startIcon={<AddCircleOutlineIcon sx={{ fontSize: "16px" }} />}
        onClick={handleCreateAndSelect}
        disabled={!title.trim()}
        sx={{ flexShrink: 0 }}
      >
        Create
      </Button>
    </Stack>
  );
};
